import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SEO_PAGES, SEO_REDIRECTS } from "./pages/seo/seoContent";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./hooks/useAuth";
import { AdminLayout } from "./components/admin/AdminLayout";
import { ScrollToTop } from "./components/ScrollToTop";
import { RecoveryRedirect } from "./components/RecoveryRedirect";
import { Analytics } from "./components/Analytics";
import { RouteTransition } from "./components/RouteTransition";
import { CookieBanner } from "./components/CookieBanner";
import { TurnstileProvider } from "./components/TurnstileProvider";
import Landing from "./pages/Landing";
const Wizard = lazy(() => import("./pages/Wizard"));
const Bestaetigung = lazy(() => import("./pages/Bestaetigung"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminProjekte = lazy(() => import("./pages/AdminProjekte"));
const AdminKunden = lazy(() => import("./pages/AdminKunden"));
const AdminAnbieter = lazy(() => import("./pages/AdminAnbieter"));
const AdminRechnungen = lazy(() => import("./pages/AdminRechnungen"));
const AdminAuswertungen = lazy(() => import("./pages/AdminAuswertungen"));
const ProjektDetail = lazy(() => import("./pages/ProjektDetail"));
const AnbieterLogin = lazy(() => import("./pages/AnbieterLogin"));
const AnbieterDashboard = lazy(() => import("./pages/AnbieterDashboard"));
const AnbieterPasswortVergessen = lazy(() => import("./pages/AnbieterPasswortVergessen"));
const AnbieterPasswortZuruecksetzen = lazy(() => import("./pages/AnbieterPasswortZuruecksetzen"));

const NotFound = lazy(() => import("./pages/NotFound"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Impressum = lazy(() => import("./pages/Impressum"));
const AGB = lazy(() => import("./pages/AGB"));
const FuerAnbieter = lazy(() => import("./pages/FuerAnbieter"));
const Liftarten = lazy(() => import("./pages/Liftarten"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const SeoRoute = lazy(() => import("./pages/seo/SeoRoute"));
const KundenRueckmeldung = lazy(() => import('./pages/KundenRueckmeldung'));

const queryClient = new QueryClient();

const withAdminLayout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TurnstileProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <RecoveryRedirect />
          <Analytics />
          <Suspense fallback={null}>
            <RouteTransition>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/wizard" element={<Wizard />} />
              <Route path="/rueckmeldung/:antwort" element={<KundenRueckmeldung />} />
              <Route path="/bestaetigung/:projektId" element={<Bestaetigung />} />

              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/agb" element={<AGB />} />
              <Route path="/fuer-anbieter" element={<FuerAnbieter />} />
              {/* Eigene Seite entfernt: Anker auf der Startseite */}
              <Route path="/so-funktioniert" element={<Navigate to="/#so-funktioniert" replace />} />
              <Route path="/liftarten" element={<Liftarten />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

              {/* Öffentliche Themenseiten */}
              {SEO_PAGES.map((p) => (
                <Route key={p.slug} path={p.slug} element={<SeoRoute />} />
              ))}
              {/* Zusammengelegte Themenseiten */}
              {SEO_REDIRECTS.map((r) => (
                <Route key={r.from} path={r.from} element={<Navigate to={r.to} replace />} />
              ))}

              <Route path="/auth" element={<Auth />} />

              {/* Admin routes */}
              <Route path="/admin" element={withAdminLayout(<AdminDashboard />)} />
              <Route path="/admin/projekte" element={withAdminLayout(<AdminProjekte />)} />
              <Route path="/admin/kunden" element={withAdminLayout(<AdminKunden />)} />
              <Route path="/admin/anbieter" element={withAdminLayout(<AdminAnbieter />)} />
              <Route path="/admin/rechnungen" element={withAdminLayout(<AdminRechnungen />)} />
              <Route path="/admin/auswertungen" element={withAdminLayout(<AdminAuswertungen />)} />
              <Route path="/admin/projekt/:id" element={withAdminLayout(<ProjektDetail />)} />

              <Route path="/anbieter/login" element={<AnbieterLogin />} />
              <Route path="/anbieter/dashboard" element={<AnbieterDashboard />} />
              <Route path="/anbieter/passwort-vergessen" element={<AnbieterPasswortVergessen />} />
              <Route path="/anbieter/passwort-zuruecksetzen" element={<AnbieterPasswortZuruecksetzen />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </RouteTransition>
          </Suspense>
          <CookieBanner />
        </BrowserRouter>
      </TooltipProvider>
      </TurnstileProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
